import React, {useEffect, useRef, useState} from 'react';
import {FontAwesomeIcon} from '@fortawesome/react-fontawesome';
import {faBuilding, faCheck, faEnvelope} from '@fortawesome/free-solid-svg-icons';
import {Button, Card, Col, Container, Form, InputGroup, Row} from '@themesberg/react-bootstrap';
import {Link, useHistory, useLocation} from 'react-router-dom';
import {FormattedMessage} from 'react-intl';

import {Routes} from '../../routes';
import API from '../../services/API';
import Utils from '../../services/Utils';
import Countries from '../../components/Countries';
import {resolveVatRate} from '../../services/vat';
import BrandLockup from '../../components/BrandLockup';

const PLAN_FEATURES = {
  FREE: ['FEAT_BOOKINGS_5', 'FEAT_VEHICLE_1', 'FEAT_MODULE_1', 'FEAT_DRIVER_PAYMENT'],
  MONTHLY: ['FEAT_BOOKINGS_UNLIMITED', 'FEAT_VEHICLES_UNLIMITED', 'FEAT_MODULE_UNLIMITED', 'FEAT_ONLINE_PAYMENT', 'FEAT_CALENDAR_SYNC'],
  ANNUAL: ['FEAT_BOOKINGS_UNLIMITED', 'FEAT_VEHICLES_UNLIMITED', 'FEAT_MODULE_UNLIMITED', 'FEAT_ONLINE_PAYMENT', 'FEAT_CALENDAR_SYNC'],
};

export default (props) => {
  Utils.setBackground(true);
  const {intl} = props;
  const history = useHistory();
  // Formule présélectionnée depuis le CTA du site vitrine : `.../signup?plan=free|monthly|annual`
  // (HashRouter → la query vit après le hash, ex. `#/subscription/signup?plan=annual`). Insensible à
  // la casse, retombe sur le payant mensuel si le paramètre est absent ou inconnu.
  const planParam = (new URLSearchParams(useLocation().search).get('plan') || '').toUpperCase();
  const initialPlan = ['FREE', 'MONTHLY', 'ANNUAL'].includes(planParam) ? planParam : 'MONTHLY';
  const [plan, setPlan] = useState(initialPlan); // on met le payant en avant par défaut
  // Jamais de mot de passe au formulaire : un mot de passe temporaire est envoyé par email (gratuit
  // comme payant). Le nom commercial est saisi à la première connexion (onboarding).
  // La raison sociale n'est demandée qu'en formule payante : elle figure sur la facture d'abonnement,
  // alors que le gratuit ne facture rien. L'adresse de facturation n'est PAS demandée ici — le Checkout
  // Stripe l'exige de toute façon à l'écran suivant, la saisir deux fois ferait fuir.
  const [formData, setFormData] = useState({
    email: '', country: 'FR', vatNumber: '', legalName: '',
  });
  const [prices, setPrices] = useState(null);
  const [loading, setLoading] = useState(false);
  const [accountCreated, setAccountCreated] = useState(false);

  useEffect(() => {
    Utils.checkAccessTokenForLogin(history);
    API.getSubscriptionPrices().then(r => {
      if (r && r.data && r.data.success === true) {
        setPrices(r.data.data);
      }
    }).catch(() => {});
  }, [history]);

  const isFree = plan === 'FREE';
  const vat = resolveVatRate(formData.country, formData.vatNumber);
  const update = (field) => (e) => setFormData({...formData, [field]: e.target.value});

  // Capture du prospect dès que l'email est saisi, sans attendre la validation du formulaire : c'est
  // justement la personne qui abandonne en cours de route qu'on veut pouvoir relancer. L'appel est
  // « best effort » (erreurs avalées côté API) et dédoublonné côté serveur sur l'email.
  const lastCapture = useRef(null);
  const captureProspect = (email, wantedPlan, country) => {
    if (!email || !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email)) return;
    const signature = `${email}|${wantedPlan}|${country}`;
    if (lastCapture.current === signature) return;
    lastCapture.current = signature;
    API.postSubscriptionProspect({email, countryCode: country, language: intl.locale, plan: wantedPlan});
  };

  const fmt = new Intl.NumberFormat(intl.locale || 'fr', {style: 'currency', currency: (prices && prices.currency) || 'EUR'});
  const monthly = prices ? parseFloat(prices.monthly) : null;
  const annual = prices ? parseFloat(prices.annual) : null;
  const annualPerMonth = annual != null ? annual / 12 : null;
  const annualSave = (monthly != null && annual != null) ? (monthly * 12 - annual) : null;

  const priceLabel = (p) => {
    if (p === 'FREE') return '0 €';
    const amount = p === 'ANNUAL' ? annual : monthly;
    return amount != null ? fmt.format(amount) : '—';
  };

  const handleSubmit = (e) => {
    e.preventDefault();
    setLoading(true);
    // Filet : si l'utilisateur a soumis sans jamais quitter le champ email (autofill + Entrée).
    captureProspect(formData.email, plan, formData.country);

    if (isFree) {
      API.postSignupFree({
        email: formData.email, countryCode: formData.country, language: intl.locale,
      }).then((response) => {
        setLoading(false);
        if (response && response.data && response.data.success === true) {
          // Pas de connexion immédiate : les identifiants sont envoyés par email.
          setAccountCreated(true);
        } else {
          Utils.notifyError(intl.formatMessage({id: 'ERROR'}), intl.formatMessage({id: 'SIGNUP_EMAIL_USED'}));
        }
      }).catch((error) => {
        setLoading(false);
        const conflict = error.response && error.response.status === 409;
        Utils.notifyError(intl.formatMessage({id: 'ERROR'}), intl.formatMessage({id: conflict ? 'SIGNUP_EMAIL_USED' : 'ERROR_OCCURRED'}));
      });
    } else {
      // `name` porte la raison sociale : elle est stockée sur le prospect et reprise comme nom du
      // compte à la création (provisionSubscriber), au lieu du nom par défaut.
      API.postSubscriptionCheckout({
        plan, email: formData.email, name: formData.legalName,
        countryCode: formData.country, vatNumber: formData.vatNumber, language: intl.locale,
        legalName: formData.legalName,
      }).then((response) => {
        setLoading(false);
        if (response && response.data && response.data.success === true) {
          window.location.replace(response.data.data);
        } else {
          const billingIssue = response && response.data && response.data.data === 'BILLING_INFO_REQUIRED';
          Utils.notifyError(intl.formatMessage({id: 'ERROR'}), intl.formatMessage({id: billingIssue ? 'BILLING_INFO_REQUIRED' : 'ERROR_OCCURRED'}));
        }
      }).catch(() => {
        setLoading(false);
        Utils.notifyError(intl.formatMessage({id: 'ERROR'}), intl.formatMessage({id: 'ERROR_OCCURRED'}));
      });
    }
  };

  const planCard = (p) => (
    <div key={p}
         className={`cc-plan-card cc-plan-selectable ${plan === p ? 'is-selected' : ''} ${p === 'ANNUAL' ? 'is-popular' : ''}`}
         onClick={() => {
           setPlan(p);
           // Le prospect a peut-être déjà saisi son email : on met à jour la formule visée.
           captureProspect(formData.email, p, formData.country);
         }} role="button">
      {p === 'ANNUAL' && <span className="cc-plan-badge"><FormattedMessage id="PLAN_ANNUAL_TAGLINE"/></span>}
      <div className="cc-plan-select-dot"/>
      <div className="cc-plan-name"><FormattedMessage id={`PLAN_${p}`}/></div>
      <div className="cc-plan-price">
        {priceLabel(p)}
        {p !== 'FREE' && <span className="cc-plan-price-suffix"> {intl.formatMessage({id: 'HT_LABEL'})}{intl.formatMessage({id: p === 'ANNUAL' ? 'PER_YEAR' : 'PER_MONTH'})}</span>}
      </div>
      <div className="cc-plan-extra">
        {p === 'ANNUAL' && annualPerMonth != null && <span className="cc-plan-equiv"><FormattedMessage id="EQUIV_PER_MONTH" values={{price: fmt.format(annualPerMonth)}}/></span>}
        {p === 'ANNUAL' && annualSave != null && annualSave > 0 && <span className="cc-plan-save"><FormattedMessage id="SAVE_AMOUNT" values={{amount: fmt.format(annualSave)}}/></span>}
        {p === 'FREE' && <span className="cc-plan-tagline"><FormattedMessage id="PLAN_FREE_TAGLINE"/></span>}
        {p === 'MONTHLY' && <span className="cc-plan-tagline"><FormattedMessage id="PLAN_MONTHLY_TAGLINE"/></span>}
      </div>
      <ul className="cc-plan-features">
        {PLAN_FEATURES[p].map(f => <li key={f}><FontAwesomeIcon icon={faCheck}/> <FormattedMessage id={f}/></li>)}
      </ul>
    </div>
  );

  return (
    <main>
      <section className="d-flex align-items-center my-5 mt-lg-5 mb-lg-5">
        <Container>
          <BrandLockup/>
          <Row className="justify-content-center">
            <Col xs={12} className="d-flex align-items-center justify-content-center">
              <div className="bg-white shadow-soft border rounded border-light p-4 p-lg-5 w-100 cc-signup-card">
                {accountCreated ? (
                <div className="cc-signup-confirm text-center">
                  <FontAwesomeIcon icon={faEnvelope} className="cc-signup-confirm-icon"/>
                  <h3 className="mb-2"><FormattedMessage id="SIGNUP_DONE_TITLE"/></h3>
                  <p className="text-muted"><FormattedMessage id="SIGNUP_DONE_BODY" values={{email: <strong>{formData.email}</strong>}}/></p>
                  <Card.Link as={Link} to={Routes.Signin.path}><FormattedMessage id="SIGN_IN"/></Card.Link>
                </div>
                ) : (
                <>
                <div className="text-center mb-4">
                  <h3 className="mb-1"><FormattedMessage id="SIGNUP_TITLE"/></h3>
                  <p className="text-muted mb-0"><FormattedMessage id="SIGNUP_COMMERCIAL_SUBTITLE"/></p>
                </div>

                <div className="cc-pricing cc-pricing-3 mb-4">
                  {['FREE', 'MONTHLY', 'ANNUAL'].map(planCard)}
                </div>

                <Form onSubmit={handleSubmit}>
                  <Row>
                    <Col md={6}>
                      <Form.Group id="email" className="mb-3">
                        <Form.Label><FormattedMessage id="EMAIL_ADDRESS"/></Form.Label>
                        <InputGroup><InputGroup.Text><FontAwesomeIcon icon={faEnvelope}/></InputGroup.Text>
                          <Form.Control required type="email" placeholder="example@company.com" value={formData.email}
                                        onChange={update('email')}
                                        onBlur={() => captureProspect(formData.email, plan, formData.country)}/></InputGroup>
                      </Form.Group>
                    </Col>
                    <Col md={6}>
                      <Form.Group id="country" className="mb-3">
                        <Form.Label><FormattedMessage id="COUNTRY"/></Form.Label>
                        <Countries id="country" value={formData.country} onChange={update('country')} intl={intl}/>
                      </Form.Group>
                    </Col>
                  </Row>
                  {/* Raison sociale : obligatoire en formule payante, car c'est elle qui figure sur la
                      facture d'abonnement — le Checkout Stripe, lui, ne collecte que le nom du porteur
                      de la carte. L'adresse est demandée par Stripe à l'écran suivant. La formule
                      gratuite ne facture rien et reste volontairement en inscription courte. */}
                  {!isFree && (
                    <>
                      <Form.Group id="legalName" className="mb-3">
                        <Form.Label><FormattedMessage id="LEGAL_NAME"/></Form.Label>
                        <InputGroup><InputGroup.Text><FontAwesomeIcon icon={faBuilding}/></InputGroup.Text>
                          <Form.Control required type="text" value={formData.legalName}
                                        onChange={update('legalName')}/></InputGroup>
                        <Form.Text className="text-muted"><FormattedMessage id="LEGAL_NAME_HELP"/></Form.Text>
                      </Form.Group>
                      <Form.Group id="vatNumber" className="mb-3">
                        <Form.Label><FormattedMessage id="VAT_NUMBER"/></Form.Label>
                        <InputGroup><InputGroup.Text><FontAwesomeIcon icon={faBuilding}/></InputGroup.Text>
                          <Form.Control type="text" value={formData.vatNumber} onChange={update('vatNumber')}/></InputGroup>
                        <Form.Text className="text-muted"><FormattedMessage id="VAT_NUMBER_HELP"/></Form.Text>
                      </Form.Group>
                    </>
                  )}

                  {!isFree && (
                    <div className="cc-signup-vat mb-3">
                      <div className="d-flex justify-content-between align-items-center">
                        <span className="text-muted"><FormattedMessage id="VAT_APPLICABLE"/></span>
                        <span className="fw-bold">{`${vat.rate} %`}</span>
                      </div>
                      <small className="text-muted d-block"><FormattedMessage id="VAT_ON_STRIPE_NOTE"/></small>
                    </div>
                  )}

                  <Button variant="primary" type="submit" className="w-100" disabled={loading}>
                    {loading && <i className="spinner-border spinner-border-sm text-light"/>}
                    {!loading && <FormattedMessage id={isFree ? 'CREATE_FREE_ACCOUNT' : 'CONTINUE_TO_PAYMENT'}/>}
                  </Button>

                  {/* Information RGPD (art. 13) : finalité, absence de cession, droit d'effacement. */}
                  <p className="text-muted text-center mt-3 mb-0" style={{fontSize: '.75rem', lineHeight: 1.5}}>
                    <FormattedMessage id="SIGNUP_PRIVACY_NOTICE"/>
                  </p>
                </Form>

                <div className="text-center mt-4">
                  <FormattedMessage id="ALREADY_HAVE_ACCOUNT"/>{' '}
                  <Card.Link as={Link} to={Routes.Signin.path}><FormattedMessage id="SIGN_IN"/></Card.Link>
                </div>
                </>
                )}
              </div>
            </Col>
          </Row>
        </Container>
      </section>
    </main>
  );
};
